/* Core storefront interactions: navigation, cart, forms, filters.
   Loaded on every page. Vanilla, no dependencies, progressive enhancement only.
   Section-level components live in sections.js. */
(() => {
  'use strict';

  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.lastFocus = null;
      // Delegated: the close button and "Continue shopping" are re-rendered on every
      // cart refresh, and listeners bound to the old elements died with them
      // (2026-09-27: neither button worked after a quantity change).
      on(this, 'click', (e) => {
        if (e.target.closest('[data-close-cart]')) this.close();
      });
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
      on(this, 'keydown', (e) => this.trapFocus(e));
    }

    open() {
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      const first = $(FOCUSABLE, this);
      if (first) first.focus();
    }

    close() {
      this.hidden = true;
      document.body.style.overflow = '';
      $$('[data-toggle="cart-drawer"]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (this.lastFocus) this.lastFocus.focus();
    }

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, this).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const drawer = $('cart-drawer');
  $$('[data-toggle="cart-drawer"]').forEach((trigger) => {
    on(trigger, 'click', (e) => {
      if (!drawer) return;
      e.preventDefault();
      trigger.setAttribute('aria-expanded', 'true');
      drawer.open();
    });
  });

  /* ---------- Add to cart ---------- */
  $$('.product-form').forEach((form) => {
    if (!drawer) return;
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const button = $('[name="add"]', form);
      if (button) button.disabled = true;
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        drawer.open();
      } catch {
        form.submit();
      } finally {
        if (button) button.disabled = false;
      }
    });
  });

  async function refreshCart() {
    if (!drawer) return;
    const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
    if (res.ok) {
      const data = await res.json();
      const html = data['cart-drawer'];
      if (html) {
        // Swap only the contents and the footer. Replacing the whole drawer recreated
        // the panel and replayed its slide-in on every quantity change (2026-09-27).
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const freshBody = $('[data-cart-contents]', parsed);
        const body = $('[data-cart-contents]', drawer);
        if (freshBody && body) body.innerHTML = freshBody.innerHTML;
        const freshFooter = $('.cart-drawer__footer', parsed);
        const footer = $('.cart-drawer__footer', drawer);
        if (footer && freshFooter) footer.replaceWith(freshFooter);
        else if (footer) footer.remove();
        else if (freshFooter) $('.cart-drawer__panel', drawer)?.append(freshFooter);
      }
    }
    const cart = await (await fetch('/cart.js')).json();
    $$('[data-cart-count]').forEach((el) => (el.textContent = cart.item_count));
  }

  /* ---------- Cart drawer quantities ----------
     The drawer's number inputs never had a handler, so changing a quantity or trying
     to remove a line did nothing (found 2026-09-27). Delegated on the drawer, because
     refreshCart replaces its whole innerHTML. */
  async function changeLine(key, quantity) {
    if (!drawer) return;
    drawer.classList.add('is-busy');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('change failed');
      await refreshCart();
    } catch {
      window.location.href = '/cart';
    } finally {
      drawer.classList.remove('is-busy');
    }
  }
  on(drawer, 'click', (e) => {
    const step = e.target.closest('[data-cart-step]');
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) return changeLine(remove.dataset.cartRemove, 0);
    if (!step) return;
    const input = $('[data-cart-quantity]', step.closest('[data-cart-stepper]'));
    if (!input) return;
    const next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.cartStep));
    input.value = next;
    changeLine(input.dataset.cartQuantity, next);
  });
  on(drawer, 'change', (e) => {
    const input = e.target.closest('[data-cart-quantity]');
    if (input) changeLine(input.dataset.cartQuantity, Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* ---------- Forms that submit themselves ---------- */
  $$('[data-auto-submit]').forEach((control) => {
    on(control, 'change', () => control.form && control.form.submit());
  });

  /* ---------- Facets ---------- */
  // Hàng thẻ cuộn ngang (điện thoại): kéo thẻ đang chọn vào giữa tầm nhìn, nếu không
  // "Christmas and Winter" đang chọn có thể nằm khuất bên phải và người xem tưởng chưa lọc.
  // Chỉ chỉnh scrollLeft của hàng, không dùng scrollIntoView vì nó kéo cả trang xuống.
  $$('.facets__links a.is-active').forEach((link) => {
    const row = link.closest('ul');
    if (!row || row.scrollWidth <= row.clientWidth) return;
    row.scrollLeft = link.offsetLeft - (row.clientWidth - link.offsetWidth) / 2;
  });
  // "Show N more" của danh sách mẫu (máy tính). Không gập khi mẫu đang chọn nằm trong
  // phần thừa, nếu không người xem không thấy mình đang lọc theo gì.
  $$('[data-links-more]').forEach((button) => {
    const nav = button.closest('.facets__links');
    const extras = $$('.facets__links-extra', nav);
    if (!extras.length || extras.some((li) => $('.is-active', li))) return;
    nav.classList.add('is-collapsed');
    button.hidden = false;
    on(button, 'click', () => {
      const collapsed = nav.classList.toggle('is-collapsed');
      button.setAttribute('aria-expanded', String(!collapsed));
      button.textContent = collapsed ? button.dataset.more : button.dataset.less;
    });
  });
  on($('[data-toggle="facets"]'), 'click', (e) => {
    const button = e.currentTarget;
    const form = document.getElementById(button.getAttribute('aria-controls'));
    if (!form) return;
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!open));
    form.classList.toggle('is-open', !open);
  });

  /* ---------- Variant picker ---------- */
  $$('.variant-picker').forEach((picker) => {
    const dataEl = $('[data-variant-data]', picker);
    if (!dataEl) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    const form = picker.closest('form');
    const idInput = form && $('[data-variant-input]', form);

    on(picker, 'change', () => {
      const chosen = $$('.variant-picker__input:checked', picker)
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);

      $$('[data-selected-for]', picker).forEach((el, i) => {
        el.textContent = chosen[i] ?? '';
      });

      const match = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
      if (!match) return;

      if (idInput) idInput.value = match.id;
      const button = form && $('[name="add"]', form);
      if (button) button.disabled = !match.available;

      // Price differs by variant (a larger suncatcher costs more). Without this the page
      // kept the first variant's price and checkout charged another.
      const scope = picker.closest('.shopify-section') || document;
      const price = $('.price', scope);
      if (price && match.price_html) price.outerHTML = match.price_html;

      if (picker.closest('.main-product-section')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }
    });
  });
})();
